/**
 * Square API Service
 * 
 * This service handles all interactions with the Square API for inventory management.
 * 
 * SETUP INSTRUCTIONS:
 * 1. Get your Square Access Token from https://developer.squareup.com/
 * 3. Set environment variable: VITE_SQUARE_LOCATION_ID
 * 
 * For development, this uses localStorage to simulate Square API.
 */

//const SQUARE_API_BASE = 'https://cors-anywhere.com/https://connect.squareup.com/v2';
const SQUARE_API_BASE = import.meta.env.VITE_SQUARE_PROXY;
const USE_MOCK = !import.meta.env.VITE_IS_PROD || import.meta.env.VITE_IS_PROD == 'false'; // Use mock if no token

class SquareAPI {
  constructor() {
    this.locationId = import.meta.env.VITE_SQUARE_LOCATION_ID;
    this.brandDefinition = null; // cached: { id, version, selections: [{uid, name}] }
    this.categoriesCache = null; // cached categories

    if (USE_MOCK) {
      console.warn('Using mock Square API - enable PROD for production');
      this.initMockStorage();
    }
  }

  isMock(){
    return USE_MOCK;
  }

  /**
   * Initialize mock storage for development
   */
  initMockStorage() {
    if (!localStorage.getItem('square_items')) {
      localStorage.setItem('square_items', JSON.stringify([]));
    }
    if (!localStorage.getItem('square_brands')) {
      localStorage.setItem('square_brands', JSON.stringify([]));
    }
    if (!localStorage.getItem('square_categories')) {
      localStorage.setItem('square_categories', JSON.stringify([]));
    }
    if (!localStorage.getItem('square_transactions')) {
      localStorage.setItem('square_transactions', JSON.stringify([]));
    }
  }

  /**
   * Get all catalog items
   */
  async getItems() {
    if (USE_MOCK) {
      return this.getMockItems();
    }

    try {
      // Ensure brand definition is loaded so we can resolve brand UIDs during transform
      await this.ensureBrandDefinition();

      const response = await fetch(`${SQUARE_API_BASE}/catalog/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      const data = await response.json();
      const items = this.transformSquareItems(data.objects || []);

      // Square stores inventory separately from catalog. Fetch live counts
      // and merge them in so all consumers have correct quantities.
      const variationIds = items
        .flatMap(item => (item.variations || []).map(v => v.id))
        .filter(Boolean);

      if (variationIds.length > 0) {
        const counts = await this.getInventoryCounts(variationIds);
        items.forEach(item => {
          (item.variations || []).forEach(v => {
            if (v.id in counts) v.quantity = counts[v.id];
          });
        });
      }

      return items;
    } catch (error) {
      console.error('Error fetching items from Square:', error);
      throw error;
    }
  }

  /**
   * Create a new catalog item
   */
  async createItem(item) {
    if (USE_MOCK) {
      return this.createMockItem(item);
    }

    try {
      const catalogObject = await this.transformToSquareItem(item);
      
      const response = await fetch(`${SQUARE_API_BASE}/catalog/object`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idempotency_key: this.generateIdempotencyKey(),
          object: catalogObject
        })
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      const data = await response.json();
      const createdItem = this.transformSquareItem(data.catalog_object);

      // Set inventory quantities via the Square Inventory API.
      // Match by SKU so order differences between Square's response and our input don't matter.
      const variationQuantities = createdItem.variations.map(v => {
        const original = item.variations.find(orig => orig.sku === v.sku);
        return { id: v.id, quantity: original?.quantity || 0 };
      });

      await this.setInventoryQuantities(variationQuantities);

      // Return the item with quantities populated from the original input.
      return {
        ...createdItem,
        variations: createdItem.variations.map(v => {
          const original = item.variations.find(orig => orig.sku === v.sku);
          return { ...v, quantity: original?.quantity || 0 };
        })
      };
    } catch (error) {
      console.error('Error creating item in Square:', error);
      throw error;
    }
  }

  /**
   * Update a catalog item
   */
  async updateItem(itemId, updates) {
    if (USE_MOCK) {
      return this.updateMockItem(itemId, updates);
    }

    try {
      const catalogObject = await this.transformToSquareItem(updates, itemId);
      
      const response = await fetch(`${SQUARE_API_BASE}/catalog/object`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idempotency_key: this.generateIdempotencyKey(),
          object: catalogObject
        })
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      const data = await response.json();
      return this.transformSquareItem(data.catalog_object);
    } catch (error) {
      console.error('Error updating item in Square:', error);
      throw error;
    }
  }

  /**
   * Delete a catalog item
   */
  async deleteItem(itemId) {
    if (USE_MOCK) {
      return this.deleteMockItem(itemId);
    }

    try {
      const response = await fetch(`${SQUARE_API_BASE}/catalog/object/${itemId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting item from Square:', error);
      throw error;
    }
  }

  /**
   * Transform Square catalog item to our format
   */
  transformSquareItem(squareItem) {
    const itemData = squareItem.item_data || {};
    const variations = (itemData.variations || []).map(v => ({
      id: v.id,
      color: v.item_variation_data?.name?.split(' - ')[0] || '',
      size: v.item_variation_data?.name?.split(' - ')[1] || '',
      sku: v.item_variation_data?.sku || '',
      price: v.item_variation_data?.price_money?.amount / 100 || 0,
      cost: v.item_variation_data?.cost_money?.amount / 100 || null,
      quantity: 0 // Populated by getInventoryCounts() after catalog fetch
    }));



    let brand = '';
    const brandAttr = itemData.custom_attribute_values?.brand;
    if (brandAttr) {
      if (brandAttr.selection_uid_values?.length) {
        const uid = brandAttr.selection_uid_values[0];
        const match = this.brandDefinition?.selections.find(s => s.uid === uid);
        brand = match ? match.name : '';
      } else if (brandAttr.string_value) {
        brand = brandAttr.string_value;
      }
    }

    // Extract category name from category_id if available
    let category = '';
    if (itemData.category_id) {
      category = itemData.category_id; // Store the category ID for now
    }

    // Extract cost from first variation (assuming all variations have same cost)
    let cost = variations[0]?.cost || null;

    return {
      id: squareItem.id,
      name: itemData.name || '',
      sku: itemData.sku || '',
      brand: brand,
      category: category,
      defaultPrice: variations[0]?.price || 0,
      cost: cost,
      variations: variations,
      createdAt: squareItem.created_at
    };
  }

  /**
   * Transform Square catalog items array
   */
  transformSquareItems(squareItems) {
    return squareItems
      .filter(item => item.type === 'ITEM')
      .map(item => this.transformSquareItem(item));
  }

  /**
   * Transform our item format to Square catalog object
   */
  async transformToSquareItem(item, itemId = null) {
    const variations = (item.variations || []).map(v => {
      const variationData = {
        type: 'ITEM_VARIATION',
        id: v.id || `#${v.sku}`,
        item_variation_data: {
          name: `${v.color} - ${v.size}`,
          sku: v.sku,
          pricing_type: 'FIXED_PRICING',
          price_money: {
            amount: Math.round(v.price * 100),
            currency: 'CAD'
          }
        }
      };

      // Add cost_money if provided
      if (item.cost !== null && item.cost !== undefined) {
        variationData.item_variation_data.cost_money = {
          amount: Math.round(item.cost * 100),
          currency: 'CAD'
        };
      }

      return variationData;
    });

    const itemDataObj = {
      name: item.name,
      sku: item.sku,
      variations: variations
    };

    // Add category if provided
    if (item.category) {
      // In mock mode, just store the category name
      if (USE_MOCK) {
        itemDataObj.category = item.category;
      } else {
        // In real mode, we need to get the category ID
        const categories = await this.getCategories();
        if (categories.includes(item.category)) {
          // Find category ID by name
          const response = await fetch(`${SQUARE_API_BASE}/catalog/list?types=CATEGORY`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const data = await response.json();
            const categoryObj = (data.objects || []).find(
              obj => obj.type === 'CATEGORY' && obj.category_data?.name === item.category
            );
            if (categoryObj) {
              itemDataObj.category_id = categoryObj.id;
            }
          }
        }
      }
    }

    if (item.brand) {
      if (!USE_MOCK && this.brandDefinition) {
        const sel = this.brandDefinition.selections.find(s => s.name === item.brand);
        if (sel) {
          itemDataObj.custom_attribute_values = {
            brand: { selection_uid_values: [sel.uid] }
          };
        }
      } else {
        itemDataObj.custom_attribute_values = {
          brand: { string_value: item.brand }
        };
      }
    }

    return {
      type: 'ITEM',
      id: itemId || '#temp_id',
      custom_attribute_values: itemDataObj.custom_attribute_values,
      item_data: itemDataObj
    };
  }

  /**
   * Set inventory quantities for catalog item variations via the Square Inventory API.
   * Square stores inventory separately from catalog objects, so this must be called
   * after createItem to record the initial stock counts.
   *
   * @param {Array<{id: string, quantity: number}>} variationQuantities
   */
  async setInventoryQuantities(variationQuantities) {
    const changes = variationQuantities
      .filter(v => v.quantity > 0)
      .map(v => ({
        type: 'PHYSICAL_COUNT',
        physical_count: {
          catalog_object_id: v.id,
          state: 'IN_STOCK',
          location_id: this.locationId,
          quantity: String(v.quantity),
          occurred_at: new Date().toISOString()
        }
      }));

    if (changes.length === 0) return;

    const response = await fetch(`${SQUARE_API_BASE}/inventory/changes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        idempotency_key: this.generateIdempotencyKey(),
        changes
      })
    });

    if (!response.ok) {
      throw new Error(`Square Inventory API error: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Generate idempotency key for Square API
   */
  generateIdempotencyKey() {
    return `${Date.now()}-${Math.random().toString(36).substring(7)}`;
  }

  // ============== MOCK IMPLEMENTATION ==============

  getMockItems() {
    const items = JSON.parse(localStorage.getItem('square_items') || '[]');
    return Promise.resolve(items);
  }

  createMockItem(item) {
    const items = JSON.parse(localStorage.getItem('square_items') || '[]');
    const newItem = {
      ...item,
      id: `mock_${Date.now()}_${Math.random().toString(36).substring(7)}`
    };
    items.push(newItem);
    localStorage.setItem('square_items', JSON.stringify(items));
    return Promise.resolve(newItem);
  }

  updateMockItem(itemId, updates) {
    const items = JSON.parse(localStorage.getItem('square_items') || '[]');
    const index = items.findIndex(item => item.id === itemId);
    
    if (index === -1) {
      return Promise.reject(new Error('Item not found'));
    }

    items[index] = { ...items[index], ...updates };
    localStorage.setItem('square_items', JSON.stringify(items));
    return Promise.resolve(items[index]);
  }

  deleteMockItem(itemId) {
    const items = JSON.parse(localStorage.getItem('square_items') || '[]');
    const filtered = items.filter(item => item.id !== itemId);
    localStorage.setItem('square_items', JSON.stringify(filtered));
    return Promise.resolve({ success: true });
  }

  // ============== BRAND MANAGEMENT ==============

  /**
   * Ensure the brand custom attribute definition exists in Square's catalog.
   * Lazily creates it on first access; caches id, version, and selections.
   */
  async ensureBrandDefinition() {
    if (this.brandDefinition) return this.brandDefinition;

    // Fetch all custom attribute definitions
    const response = await fetch(`${SQUARE_API_BASE}/catalog/list?types=CUSTOM_ATTRIBUTE_DEFINITION`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Square API error: ${response.statusText}`);
    }

    const data = await response.json();
    const objects = data.objects || [];
    const existing = objects.find(
      obj => obj.custom_attribute_definition_data?.key === 'brand'
    );

    if (existing) {
      const defData = existing.custom_attribute_definition_data;
      this.brandDefinition = {
        id: existing.id,
        version: existing.version,
        selections: (defData.selection_config?.allowed_selections || []).map(s => ({
          uid: s.uid,
          name: s.name
        }))
      };
      return this.brandDefinition;
    }

    // Create the definition
    const createResponse = await fetch(`${SQUARE_API_BASE}/catalog/object`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idempotency_key: this.generateIdempotencyKey(),
        object: {
          type: 'CUSTOM_ATTRIBUTE_DEFINITION',
          id: '#brand_def',
          custom_attribute_definition_data: {
            type: 'SELECTION',
            name: 'Brand',
            key: 'brand',
            allowed_object_types: ['ITEM'],
            seller_visibility: 'SELLER_VISIBILITY_READ_WRITE_VALUES',
            app_visibility: 'APP_VISIBILITY_READ_WRITE_VALUES',
            selection_config: {
              allowed_selections: [],
              max_allowed_selections: 1
            }
          }
        }
      })
    });

    if (!createResponse.ok) {
      throw new Error(`Square API error creating brand definition: ${createResponse.statusText}`);
    }

    const createData = await createResponse.json();
    const created = createData.catalog_object;
    const createdDefData = created.custom_attribute_definition_data;
    this.brandDefinition = {
      id: created.id,
      version: created.version,
      selections: (createdDefData.selection_config?.allowed_selections || []).map(s => ({
        uid: s.uid,
        name: s.name
      }))
    };
    return this.brandDefinition;
  }

  /**
   * Upsert the brand definition with the current cached selections.
   * Updates the cache with the response.
   */
  async upsertBrandDefinition() {
    const def = this.brandDefinition;
    const response = await fetch(`${SQUARE_API_BASE}/catalog/object`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idempotency_key: this.generateIdempotencyKey(),
        object: {
          type: 'CUSTOM_ATTRIBUTE_DEFINITION',
          id: def.id,
          version: def.version,
          custom_attribute_definition_data: {
            type: 'SELECTION',
            name: 'Brand',
            key: 'brand',
            allowed_object_types: ['ITEM'],
            seller_visibility: 'SELLER_VISIBILITY_READ_WRITE_VALUES',
            app_visibility: 'APP_VISIBILITY_READ_WRITE_VALUES',
            selection_config: {
              allowed_selections: def.selections.map(s => ({ uid: s.uid, name: s.name })),
              max_allowed_selections: 1
            }
          }
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Square API error upserting brand definition: ${response.statusText}`);
    }

    const data = await response.json();
    const updated = data.catalog_object;
    const updatedDefData = updated.custom_attribute_definition_data;
    this.brandDefinition = {
      id: updated.id,
      version: updated.version,
      selections: (updatedDefData.selection_config?.allowed_selections || []).map(s => ({
        uid: s.uid,
        name: s.name
      }))
    };
    return this.brandDefinition;
  }

  async getBrands() {
    if (USE_MOCK) {
      return JSON.parse(localStorage.getItem('square_brands') || '[]');
    }
    const def = await this.ensureBrandDefinition();
    return def.selections.map(s => s.name).sort();
  }

  async addBrand(name) {
    if (USE_MOCK) {
      const brands = JSON.parse(localStorage.getItem('square_brands') || '[]');
      if (!brands.includes(name)) {
        brands.push(name);
        brands.sort();
        localStorage.setItem('square_brands', JSON.stringify(brands));
      }
      return brands;
    }

    await this.ensureBrandDefinition();
    const exists = this.brandDefinition.selections.some(s => s.name === name);
    if (exists) {
      return this.brandDefinition.selections.map(s => s.name).sort();
    }

    const sanitized = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
    this.brandDefinition.selections.push({
      uid: `#brand_${sanitized}`,
      name
    });

    await this.upsertBrandDefinition();
    return this.brandDefinition.selections.map(s => s.name).sort();
  }

  async deleteBrand(name) {
    if (USE_MOCK) {
      const brands = JSON.parse(localStorage.getItem('square_brands') || '[]');
      const filtered = brands.filter(b => b !== name);
      localStorage.setItem('square_brands', JSON.stringify(filtered));
      return filtered;
    }

    await this.ensureBrandDefinition();
    this.brandDefinition.selections = this.brandDefinition.selections.filter(s => s.name !== name);
    await this.upsertBrandDefinition();
    return this.brandDefinition.selections.map(s => s.name).sort();
  }

  /**
   * Fetch current IN_STOCK quantities for a list of variation IDs.
   * Returns a map of { variationId -> quantity }.
   * In mock mode, variations typically lack IDs so returns an empty map
   * (callers should fall back to v.quantity on the item itself).
   */
  async getInventoryCounts(variationIds) {
    if (USE_MOCK) {
      // Mock variations don't have Square-assigned IDs; quantities are
      // already on the item objects, so return empty and let callers fall back.
      return {};
    }

    const response = await fetch(`${SQUARE_API_BASE}/inventory/counts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        catalog_object_ids: variationIds,
        states: ['IN_STOCK']
      })
    });

    if (!response.ok) {
      throw new Error(`Square Inventory API error: ${response.statusText}`);
    }

    const data = await response.json();
    const counts = {};
    (data.counts || []).forEach(c => {
      counts[c.catalog_object_id] = parseInt(c.quantity) || 0;
    });
    return counts;
  }

  // ============== SKU VERIFICATION ==============

  /**
   * Check if a SKU already exists in the catalog
   * @param {string} sku - The SKU to check
   * @returns {Promise<boolean>} True if SKU exists, false otherwise
   */
  async checkSKUExists(sku) {
    if (USE_MOCK) {
      const items = JSON.parse(localStorage.getItem('square_items') || '[]');
      return items.some(item => item.sku === sku);
    }

    try {
      const response = await fetch(`${SQUARE_API_BASE}/catalog/check-sku?sku=${encodeURIComponent(sku)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        console.error('Error checking SKU:', response.statusText);
        return false; // Assume SKU doesn't exist if check fails
      }

      const data = await response.json();
      return data.exists;
    } catch (error) {
      console.error('Error checking SKU:', error);
      return false; // Assume SKU doesn't exist if check fails
    }
  }

  // ============== CATEGORY MANAGEMENT ==============

  /**
   * Get all categories from Square catalog
   */
  async getCategories() {
    if (USE_MOCK) {
      const categories = JSON.parse(localStorage.getItem('square_categories') || '[]');
      return categories.sort();
    }

    try {
      const response = await fetch(`${SQUARE_API_BASE}/catalog/list?types=CATEGORY`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      const data = await response.json();
      const categories = (data.objects || [])
        .filter(obj => obj.type === 'CATEGORY')
        .map(obj => obj.category_data?.name || '')
        .filter(Boolean)
        .sort();

      this.categoriesCache = categories;
      return categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }

  /**
   * Add a new category to Square catalog
   */
  async addCategory(name) {
    if (USE_MOCK) {
      const categories = JSON.parse(localStorage.getItem('square_categories') || '[]');
      if (!categories.includes(name)) {
        categories.push(name);
        categories.sort();
        localStorage.setItem('square_categories', JSON.stringify(categories));
      }
      return categories;
    }

    try {
      const response = await fetch(`${SQUARE_API_BASE}/catalog/object`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idempotency_key: this.generateIdempotencyKey(),
          object: {
            type: 'CATEGORY',
            id: `#category_${Date.now()}`,
            category_data: {
              name: name
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      // Refresh categories
      return await this.getCategories();
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  }

  /**
   * Delete a category from Square catalog
   */
  async deleteCategory(name) {
    if (USE_MOCK) {
      const categories = JSON.parse(localStorage.getItem('square_categories') || '[]');
      const filtered = categories.filter(c => c !== name);
      localStorage.setItem('square_categories', JSON.stringify(filtered));
      return filtered;
    }

    try {
      // First, find the category object ID by name
      const response = await fetch(`${SQUARE_API_BASE}/catalog/list?types=CATEGORY`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      const data = await response.json();
      const categoryObj = (data.objects || []).find(
        obj => obj.type === 'CATEGORY' && obj.category_data?.name === name
      );

      if (!categoryObj) {
        throw new Error('Category not found');
      }

      // Delete the category
      const deleteResponse = await fetch(`${SQUARE_API_BASE}/catalog/object/${categoryObj.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!deleteResponse.ok) {
        throw new Error(`Square API error: ${deleteResponse.statusText}`);
      }

      // Refresh categories
      return await this.getCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  // ============== TRANSACTION/ORDER MANAGEMENT ==============

  /**
   * Create a new order/transaction
   * @param {Object} orderData - { items: [{variationId, sku, name, color, size, price, quantity}], total, paymentMethod }
   * @returns {Promise<Object>} Created transaction
   */
  async createTransaction(orderData) {
    if (USE_MOCK) {
      return this.createMockTransaction(orderData);
    }

    try {
      // Create line items for Square Orders API
      const lineItems = orderData.items.map((item, index) => ({
        uid: `line_${index}`,
        quantity: String(item.quantity),
        catalog_object_id: item.variationId,
        base_price_money: {
          amount: Math.round(item.price * 100),
          currency: 'CAD'
        }
      }));

      // Create order
      const orderResponse = await fetch(`${SQUARE_API_BASE}/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idempotency_key: this.generateIdempotencyKey(),
          order: {
            location_id: this.locationId,
            line_items: lineItems,
            state: 'COMPLETED'
          }
        })
      });

      if (!orderResponse.ok) {
        throw new Error(`Square Orders API error: ${orderResponse.statusText}`);
      }

      const orderResult = await orderResponse.json();
      const orderId = orderResult.order.id;

      // Record cash payment
      const paymentResponse = await fetch(`${SQUARE_API_BASE}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idempotency_key: this.generateIdempotencyKey(),
          source_id: 'CASH',
          amount_money: {
            amount: Math.round(orderData.total * 100),
            currency: 'CAD'
          },
          order_id: orderId,
          location_id: this.locationId
        })
      });

      if (!paymentResponse.ok) {
        throw new Error(`Square Payments API error: ${paymentResponse.statusText}`);
      }

      const paymentResult = await paymentResponse.json();

      // Adjust inventory
      await this.adjustInventoryForSale(orderData.items);

      // Return transaction data
      return {
        id: orderId,
        items: orderData.items,
        total: orderData.total,
        paymentMethod: 'CASH',
        timestamp: new Date().toISOString(),
        status: 'COMPLETED',
        paymentId: paymentResult.payment.id
      };
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  }

  /**
   * Adjust inventory quantities after a sale
   */
  async adjustInventoryForSale(items) {
    if (USE_MOCK) {
      return this.adjustMockInventory(items);
    }

    const changes = items.map(item => ({
      type: 'ADJUSTMENT',
      adjustment: {
        catalog_object_id: item.variationId,
        from_state: 'IN_STOCK',
        to_state: 'SOLD',
        location_id: this.locationId,
        quantity: String(item.quantity),
        occurred_at: new Date().toISOString()
      }
    }));

    const response = await fetch(`${SQUARE_API_BASE}/inventory/changes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        idempotency_key: this.generateIdempotencyKey(),
        changes
      })
    });

    if (!response.ok) {
      throw new Error(`Square Inventory API error: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get all transactions
   */
  async getTransactions() {
    if (USE_MOCK) {
      return this.getMockTransactions();
    }

    try {
      const response = await fetch(`${SQUARE_API_BASE}/orders/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          location_ids: [this.locationId],
          query: {
            filter: {
              state_filter: {
                states: ['COMPLETED', 'CANCELED']
              }
            },
            sort: {
              sort_field: 'CREATED_AT',
              sort_order: 'DESC'
            }
          },
          limit: 100
        })
      });

      if (!response.ok) {
        throw new Error(`Square Orders API error: ${response.statusText}`);
      }

      const data = await response.json();
      const orders = data.orders || [];

      // Transform orders to our format
      return orders.map(order => ({
        id: order.id,
        items: (order.line_items || []).map(item => ({
          name: item.name || '',
          quantity: parseInt(item.quantity) || 0,
          price: (item.base_price_money?.amount || 0) / 100
        })),
        total: (order.total_money?.amount || 0) / 100,
        paymentMethod: 'CASH',
        timestamp: order.created_at,
        status: order.state
      }));
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  /**
   * Void/cancel a transaction
   */
  async voidTransaction(transactionId) {
    if (USE_MOCK) {
      return this.voidMockTransaction(transactionId);
    }

    try {
      const response = await fetch(`${SQUARE_API_BASE}/orders/${transactionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order: {
            state: 'CANCELED',
            version: 1
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Square Orders API error: ${response.statusText}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error voiding transaction:', error);
      throw error;
    }
  }

  /**
   * Get item by SKU
   */
  async getItemBySKU(sku) {
    if (USE_MOCK) {
      const items = JSON.parse(localStorage.getItem('square_items') || '[]');
      for (const item of items) {
        const variation = item.variations.find(v => v.sku === sku);
        if (variation) {
          return {
            item,
            variation
          };
        }
      }
      return null;
    }

    try {
      const items = await this.getItems();
      for (const item of items) {
        const variation = item.variations.find(v => v.sku === sku);
        if (variation) {
          return {
            item,
            variation
          };
        }
      }
      return null;
    } catch (error) {
      console.error('Error finding item by SKU:', error);
      return null;
    }
  }

  // ============== MOCK TRANSACTION METHODS ==============

  createMockTransaction(orderData) {
    const transactions = JSON.parse(localStorage.getItem('square_transactions') || '[]');
    const transaction = {
      id: `txn_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      items: orderData.items,
      total: orderData.total,
      paymentMethod: 'CASH',
      timestamp: new Date().toISOString(),
      status: 'COMPLETED'
    };
    transactions.unshift(transaction);
    localStorage.setItem('square_transactions', JSON.stringify(transactions));

    // Reduce inventory
    this.adjustMockInventory(orderData.items);

    return Promise.resolve(transaction);
  }

  adjustMockInventory(items) {
    const allItems = JSON.parse(localStorage.getItem('square_items') || '[]');

    items.forEach(soldItem => {
      const item = allItems.find(i =>
        i.variations.some(v => v.sku === soldItem.sku)
      );
      if (item) {
        const variation = item.variations.find(v => v.sku === soldItem.sku);
        if (variation) {
          variation.quantity = Math.max(0, (variation.quantity || 0) - soldItem.quantity);
        }
      }
    });

    localStorage.setItem('square_items', JSON.stringify(allItems));
    return Promise.resolve();
  }

  getMockTransactions() {
    const transactions = JSON.parse(localStorage.getItem('square_transactions') || '[]');
    return Promise.resolve(transactions);
  }

  voidMockTransaction(transactionId) {
    const transactions = JSON.parse(localStorage.getItem('square_transactions') || '[]');
    const transaction = transactions.find(t => t.id === transactionId);

    if (transaction) {
      transaction.status = 'CANCELED';

      // Restore inventory
      const allItems = JSON.parse(localStorage.getItem('square_items') || '[]');
      transaction.items.forEach(soldItem => {
        const item = allItems.find(i =>
          i.variations.some(v => v.sku === soldItem.sku)
        );
        if (item) {
          const variation = item.variations.find(v => v.sku === soldItem.sku);
          if (variation) {
            variation.quantity = (variation.quantity || 0) + soldItem.quantity;
          }
        }
      });
      localStorage.setItem('square_items', JSON.stringify(allItems));
      localStorage.setItem('square_transactions', JSON.stringify(transactions));
    }

    return Promise.resolve({ success: true });
  }
}

export const squareAPI = new SquareAPI();
